/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import apiClient from '../lib/axios';
import type { LoginCredentials, AuthResponse } from '../types';

export const authService = {
  /**
   * Login user
   */
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const { data } = await apiClient.post<AuthResponse>('/login', credentials);
    return data;
  },

  /**
   * Logout user (revoke refresh token)
   */
  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/logout');
    } catch (error) {
      // Even if logout fails on server, we'll clear local state
      console.error('Logout error:', error);
    }
  },

  /**
   * Refresh access token using HTTP-only refresh token cookie
   */
  refresh: async (): Promise<{ token: string }> => {
    const { data } = await apiClient.post<{ token: string }>('/refresh');
    return data;
  },

  /**
   * Verify current token is valid
   */
  verifyToken: async (): Promise<boolean> => {
    try {
      await apiClient.get('/verify-token');
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Request password reset (sends email with reset link)
   */
  requestPasswordReset: async (email: string, turnstileToken?: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await apiClient.post<{ success: boolean; message: string }>(
      '/password/request-reset',
      { email, turnstile_token: turnstileToken }
    );
    return data;
  },

  /**
   * Validate password reset token (check if valid/expired)
   */
  validateResetToken: async (token: string): Promise<{ valid: boolean; error?: string }> => {
    try {
      const { data } = await apiClient.post<{ valid: boolean; error?: string }>(
        '/password/validate-token',
        { token }
      );
      return data;
    } catch {
      return { valid: false, error: 'Invalid or expired reset token' };
    }
  },

  /**
   * Reset password using token from email
   */
  resetPassword: async (token: string, password: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await apiClient.post<{ success: boolean; message: string }>(
      '/password/reset',
      { token, password }
    );
    return data;
  },

  /**
   * Change password for authenticated user
   * Requires current password verification
   */
  changePassword: async (currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await apiClient.post<{ success: boolean; message: string }>(
      '/password/change',
      { current_password: currentPassword, new_password: newPassword }
    );
    return data;
  },
};
